import React, { useEffect, useState } from "react";
import { Bot, Link2, Unlink, Loader2, RefreshCw, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WhatsAppNumberOnboard } from "@/components/WhatsAppNumberOnboard";

// الأدمن: بوتات العملاء (السكرتير التنفيذي) وربط رقم واتساب لكل بوت من الأرقام المتاحة للتوكن
export const WhatsAppTenants = () => {
  const [bots, setBots] = useState(null);
  const [nums, setNums] = useState([]);
  const [wabas, setWabas] = useState([]);
  const [showOnboard, setShowOnboard] = useState(false);
  const [pick, setPick] = useState({});
  const [sys, setSys] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [b, n] = await Promise.all([api.get("/wa-bot/admin/list"), api.get("/wa/numbers")]);
      const all = n.data.numbers || [];
      setBots(b.data); setNums(all);
      const seen = {};
      all.forEach((x) => { seen[x.waba_id] = x.waba_name; });
      (n.data.wabas || []).forEach((w) => { seen[w.id] = w.name; });
      setWabas(Object.entries(seen).map(([id, name]) => ({ id, name })));
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const attach = async (bot) => {
    const n = nums.find((x) => x.id === pick[bot.id]);
    if (!n) return;
    setBusy(bot.id);
    try { await api.post(`/wa-bot/${bot.id}/attach`, { phone_id: n.id, waba_id: n.waba_id, system: !!sys[bot.id] || !!n.active }); toast.success(`تم ربط ${n.display_phone_number} ببوت ${bot.name}`); await load(); }
    catch (e) { toast.error(formatError(e)); } finally { setBusy(""); }
  };
  const detach = async (bot) => {
    setBusy(bot.id);
    try { await api.post(`/wa-bot/${bot.id}/detach`); toast.success("تم فصل الرقم"); await load(); } catch (e) { toast.error(formatError(e)); } finally { setBusy(""); }
  };
  const taken = new Set((bots || []).map((b) => b.phone_id).filter(Boolean));

  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="wa-tenants">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2"><Bot className="w-4 h-4 text-primary" /><div><div className="font-bold text-sm">بوتات العملاء (السكرتير التنفيذي) — ربط الأرقام</div><div className="text-[11px] text-muted-foreground">كل عميل يبني معرفة بوته من مشروعه؛ هنا تربط رقم واتساب نشاطه (بعد تسجيله في حساب Meta الخاص برواج).</div></div></div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => setShowOnboard((v) => !v)} data-testid="wa-onboard-toggle"><PhoneCall className="w-3 h-3" /> إضافة رقم عميل</Button>
          <Button size="sm" variant="ghost" onClick={load} data-testid="wa-tenants-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {showOnboard && <WhatsAppNumberOnboard wabas={wabas} onDone={load} />}
      {!bots && <Loader2 className="w-5 h-5 animate-spin text-primary" />}
      {bots && bots.length === 0 && <div className="text-xs text-muted-foreground" data-testid="wa-tenants-empty">لا توجد بوتات عملاء بعد.</div>}
      {(bots || []).map((b) => (
        <div key={b.id} className="rounded-xl border border-border p-3 text-xs grid md:grid-cols-[1fr_auto] gap-3 items-center" data-testid={`wa-tenant-${b.id}`}>
          <div className="min-w-0">
            <div className="font-bold text-sm">{b.name} <span className="font-normal text-muted-foreground">· المساعد «{b.persona}»</span> {b.enabled === false && <span className="text-[10px] rounded-full bg-secondary px-2 py-0.5">متوقف</span>}</div>
            <div className="text-muted-foreground truncate">{b.owner?.email} · {(b.knowledge?.services || []).length} خدمة · {(b.knowledge?.faqs || []).length} سؤال · {b.stats?.replies || 0} رد · {b.stats?.handoffs || 0} تحويل</div>
            {b.display_phone && <div className="text-emerald-600 font-bold mt-0.5" dir="ltr" data-testid={`wa-tenant-phone-${b.id}`}>{b.display_phone} · {b.verified_name}</div>}
          </div>
          <div className="flex items-center gap-2">
            {b.phone_id ? (
              <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" disabled={busy === b.id} onClick={() => detach(b)} data-testid={`wa-tenant-detach-${b.id}`}><Unlink className="w-3.5 h-3.5" /> فصل الرقم</Button>
            ) : (
              <>
                <select value={pick[b.id] || ""} onChange={(e) => setPick({ ...pick, [b.id]: e.target.value })} className="h-8 rounded-md border border-border bg-background px-2 text-xs" dir="ltr" data-testid={`wa-tenant-number-${b.id}`}>
                  <option value="">اختر رقمًا…</option>
                  {nums.filter((n) => n.ready && !taken.has(n.id)).map((n) => <option key={n.id} value={n.id}>{n.display_phone_number} — {n.verified_name}{n.active ? " (رقم رواج/حمد — للمشروع الرئيسي فقط)" : ""}</option>)}
                </select>
                <label className="flex items-center gap-1 text-[10px] text-muted-foreground whitespace-nowrap" title="يجمع البوت معرفة حمد الحيّة (الباقات والأسعار) مع معرفة الصفحة"><input type="checkbox" checked={!!sys[b.id]} onChange={(e) => setSys({ ...sys, [b.id]: e.target.checked })} data-testid={`wa-tenant-system-${b.id}`} /> رقم رواج نفسه</label>
                <Button size="sm" className="h-8 gap-1 text-xs" disabled={busy === b.id || !pick[b.id]} onClick={() => attach(b)} data-testid={`wa-tenant-attach-${b.id}`}>{busy === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} ربط</Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
